import {
  DataBlockVariant,
  DeviceModel,
  QueryFactory,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';
import { useDataBlockTypeTranslation } from '@lmt/unicorn-shared/app/components/measurements/common';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Field } from '../common/Field';
import { Loading } from '../common/Loading';
import { Button, ButtonColor } from '../common/ui-kit/buttons/Button';
import { DropDownInput } from '../common/ui-kit/inputs/dropdown/DropDownInput';
import { useGlobalForms } from '../global-forms/useGlobalForms';
import { MappingsTable } from './components/MappingsTable';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import styles from './MappingsPage.module.scss';
import { useClaims } from '../../features/auth/operations';
import { useDeviceModels } from '../../features/mappings/operations';

export const MappingsPage: FC = React.memo(function MappingsPage() {
  const tabs = useTabs();
  const i18next = useScopedTranslation('mappings');
  const [isLoading, setLoading] = useState<boolean>(false);
  const dataBlockTypes = useEnabledDataBlockTypes();
  const translateDataBlockType = useDataBlockTypeTranslation();
  const forms = useGlobalForms();
  const modals = useModal();
  const claims = useClaims();
  const deviceModels = useDeviceModels();

  const { queryParams, setQueryParams } =
    pages.mappingsWithDeviceAndMeasurement.useParams();
  const { deviceModel, measurementTypeId } = queryParams;

  const filteredDataBlockTypes = useMemo(
    () =>
      dataBlockTypes.filter(
        (type) =>
          type.variant === DataBlockVariant.DeviceMeasurement &&
          type.device === deviceModel,
      ),
    [dataBlockTypes, deviceModel],
  );

  // Sync device model with available options
  useEffect(() => {
    const isValidDevice = deviceModels.some((d) => d.value === deviceModel);
    if (!isValidDevice) {
      setQueryParams({ deviceModel: DeviceModel.MiniScreen });
    }
  }, [deviceModel, setQueryParams]);

  // Sync measurement type with filtered options
  useEffect(() => {
    if (filteredDataBlockTypes.length === 0) return;
    const isValidType = filteredDataBlockTypes.some(
      (t) => t.id === measurementTypeId,
    );
    if (!isValidType) {
      setQueryParams({ measurementTypeId: filteredDataBlockTypes[0].id });
    }
  }, [filteredDataBlockTypes, measurementTypeId, setQueryParams]);

  const reImportAllMeasurements = useCallback(async () => {
    const confirmed = await modals.showConfirm({
      title: i18next.t('confirmations.re_import_measurements.title'),
      text: i18next.t('confirmations.re_import_measurements.text'),
      okButtonText: i18next.t('confirmations.re_import_measurements.ok_button'),
    });
    if (confirmed) {
      const processId = await QueryFactory.DataBlockClient.startReimport();
      forms.openReImportMeasurements(processId);
    }
  }, [forms, i18next, modals]);

  const openMeasurementFields = useCallback(() => {
    tabs.openTab(
      pages.dataBlockFields.link({
        variant: DataBlockVariant.DeviceMeasurement,
        deviceModel,
        typeId: measurementTypeId ?? '',
      }),
    );
  }, [tabs, deviceModel, measurementTypeId]);

  return (
    <div className={styles.storedScrolling}>
      <Loading
        title={i18next.t('loading')}
        loading={isLoading}
        testId="data-block-mappings-page"
      >
        <div className={styles.container}>
          <div className={styles.filterArea}>
            <Field title={i18next.t('device')} className={styles.filterField}>
              <DropDownInput
                useIdFunctionAsValue
                required
                variant="normal"
                getOptionLabel="displayValue"
                idFunction="value"
                options={deviceModels}
                value={deviceModel || null}
                onValueChanged={(option) =>
                  setQueryParams({ deviceModel: option.value })
                }
              />
            </Field>

            {measurementTypeId && (
              <Field
                title={i18next.t('measurement_type')}
                className={styles.filterField}
                linkProps={
                  claims.permissions.dataBlockTypeManagement
                    ? {
                        title: i18next.t('add_type_button'),
                        onClick: () =>
                          forms.openDataBlockTypeCreation(
                            DataBlockVariant.DeviceMeasurement,
                            deviceModel,
                            (newId) =>
                              setQueryParams({ measurementTypeId: newId }),
                          ),
                        className: styles.edit,
                      }
                    : undefined
                }
              >
                <DropDownInput
                  useIdFunctionAsValue
                  required
                  variant="normal"
                  getOptionLabel={translateDataBlockType as any}
                  idFunction="id"
                  options={filteredDataBlockTypes}
                  value={measurementTypeId}
                  onValueChanged={(option) =>
                    setQueryParams({ measurementTypeId: option.id })
                  }
                />
              </Field>
            )}

            {claims.permissions.dataBlockFieldManagement && (
              <Button
                title={i18next.t('data_block_fields')}
                onClick={openMeasurementFields}
              />
            )}

            {claims.permissions.reImportAllMeasurements &&
              deviceModel === DeviceModel.MiniScreen && (
                <Button
                  title={i18next.t('re_import_all_measurements')}
                  onClick={reImportAllMeasurements}
                  color={ButtonColor.Primary}
                  className={styles.button}
                />
              )}
          </div>

          <div className={styles.table}>
            {deviceModel && measurementTypeId && (
              <MappingsTable
                measurementTypeId={measurementTypeId}
                deviceModel={deviceModel}
              />
            )}
          </div>
        </div>
      </Loading>
    </div>
  );
});
